import React from 'react';
import {connect} from 'react-redux';
import './top-progress-bar.scss';

// Thin progress bar at the top of the screen, driven by the number of in-flight
// axios requests (state.loading.count). It "trickles" towards 90% while
// requests are pending and completes to 100% when they finish.
//
// Two timing safeguards:
//  - MIN_VISIBLE_MS: once shown, the bar stays for at least this long even if
//    the request finishes almost instantly, so fast requests still flash
//    visibly (otherwise only slow/heavy pages like Home would show anything).
//  - HIDE_DELAY_MS: hiding is debounced so back-to-back requests (the counter
//    briefly hitting 0 between them) don't make the bar flicker.
const MIN_VISIBLE_MS = 500;
const HIDE_DELAY_MS = 300;

class TopProgressBar extends React.Component {

    constructor(props) {
        super(props);

        this.state = {
            visible: false,
            progress: 0,
        };

        this.shownAt = 0;
        this.trickleTimer = null;
        this.completeTimer = null;
        this.hideTimer = null;
    }

    componentDidUpdate(prevProps) {
        const wasLoading = prevProps.count > 0;
        const isLoading = this.props.count > 0;

        if (isLoading && !wasLoading) {
            this.start();
        } else if (!isLoading && wasLoading) {
            this.finish();
        }
    }

    componentWillUnmount() {
        this.clearTimers();
    }

    clearTimers() {
        clearInterval(this.trickleTimer);
        clearTimeout(this.completeTimer);
        clearTimeout(this.hideTimer);
    }

    start() {
        this.clearTimers();
        this.shownAt = Date.now();
        this.setState({visible: true, progress: 10});

        this.trickleTimer = setInterval(() => {
            this.setState(s => {
                if (s.progress >= 90) {
                    return s;
                }
                // ease towards 90% so it never visually "finishes" early
                return {progress: s.progress + (90 - s.progress) * 0.1};
            });
        }, 300);
    }

    finish() {
        clearInterval(this.trickleTimer);

        // Guarantee the bar was visible for at least MIN_VISIBLE_MS before it
        // completes, so a near-instant request still produces a visible flash.
        const elapsed = Date.now() - this.shownAt;
        const wait = Math.max(0, MIN_VISIBLE_MS - elapsed);

        this.completeTimer = setTimeout(() => {
            this.setState({progress: 100});

            // Debounce the hide: if a new request starts within this window,
            // start() clears this timer and the bar keeps going (no flicker).
            this.hideTimer = setTimeout(() => {
                this.setState({visible: false, progress: 0});
            }, HIDE_DELAY_MS);
        }, wait);
    }

    render() {
        const {visible, progress} = this.state;

        return (
            <div className={`top-progress ${visible ? 'top-progress--visible' : ''}`}>
                <div className="top-progress__bar" style={{width: `${progress}%`}}/>
            </div>
        );
    }
}

const mapStateToProps = state => ({
    count: state.loading.count,
});

export default connect(mapStateToProps)(TopProgressBar);
